/**
 * Where an AI client connects, and what it is handed when it exports.
 *
 * Which columns, tables and applications an AI connection may reach is not set
 * here: each is set from the governance dialog of the thing itself, so the
 * person who owns it grants the access rather than a workspace admin granting
 * it on their behalf.
 */
import { useTranslation } from 'react-i18next'

import { CopyButton } from '@/shared/components/copy-button'
import { getRuntimeConfig } from '@/shared/config/runtime'
import { expiry, mcpServerUrl } from '../mcp'
import { useMcpExportSettings } from '../mcp-queries'

export function McpSection() {
  const { t } = useTranslation()
  const url = mcpServerUrl(getRuntimeConfig().publicBackendUrl, window.location.origin)

  return (
    <div className="space-y-4">
      <h3 className="text-base font-medium">{t('mcpServer.title')}</h3>
      <p className="text-sm text-muted-foreground">{t('mcpServer.description')}</p>

      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border bg-muted px-3 py-2 text-sm">
          {url}
        </code>
        <CopyButton value={url} label={t('mcpServer.copy')} />
      </div>

      <ExportBucket />
    </div>
  )
}

function ExportBucket() {
  const { t } = useTranslation()
  const { data: settings } = useMcpExportSettings()

  if (settings === undefined || !settings.configured) return null

  const { unit, count } = expiry(settings.url_expire_seconds)

  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 rounded-md border p-3 text-sm">
      <dt className="col-span-2 font-medium">{t('mcpServer.exportTitle')}</dt>
      <dt className="text-muted-foreground">{t('mcpServer.exportBackend')}</dt>
      <dd>{settings.storage_backend}</dd>
      <dt className="text-muted-foreground">{t('mcpServer.exportBucket')}</dt>
      <dd className="font-mono">{settings.bucket_name}</dd>
      <dt className="text-muted-foreground">{t('mcpServer.exportPrefix')}</dt>
      <dd className="font-mono">{settings.file_prefix}</dd>
      <dt className="text-muted-foreground">{t('mcpServer.exportExpiry')}</dt>
      <dd>
        {unit === 'minutes'
          ? t('mcpServer.expiryMinutes', { count })
          : t('mcpServer.expirySeconds', { count })}
      </dd>
      <dd className="col-span-2 text-muted-foreground">{t('mcpServer.exportNote')}</dd>
    </dl>
  )
}
